// src/components/ClubsList.js
import React, { useState, useEffect, useRef } from 'react';
import { useCompetition } from '../competition/CompetitionContext';
import { useAuth } from '../auth/AuthContext';
import auditLogger from '../utils/auditLogger';
import { normalizeClub, getClubContactPerson, getClubContactNumber, formatClubForAPI } from '../utils/fieldCompatibility';
import './ClubsList.css';
import ClubForm from './ClubForm';

const _host = 'chappie-demo.novosense.africa';
const _company = 'wdsf';

const ClubsList = ({ readOnly = false }) => {
  const { selectedCompetition } = useCompetition();
  const { currentUser } = useAuth();
  const [clubs, setClubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editingClub, setEditingClub] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [clubDancerCounts, setClubDancerCounts] = useState(new Map());
  const printRef = useRef();

  // Function to filter clubs based on search query
  const getFilteredClubs = () => {
    if (!searchQuery.trim()) {
      return clubs;
    }

    const query = searchQuery.toLowerCase().trim();

    return clubs.filter(club => {
      // Normalize club for consistent field access
      const normalizedClub = normalizeClub(club);

      // Search by club name
      if (normalizedClub.name && normalizedClub.name.toLowerCase().includes(query)) {
        return true;
      }

      // Search by contact person
      if (normalizedClub.contactPerson && normalizedClub.contactPerson.toLowerCase().includes(query)) {
        return true;
      }

      // Search by contact number
      if (normalizedClub.contactNumber && normalizedClub.contactNumber.includes(query)) {
        return true;
      }

      // Search by province
      if (normalizedClub.province && normalizedClub.province.toLowerCase().includes(query)) {
        return true;
      }

      return false;
    });
  };

  // Calculate dancer counts for each club
  const calculateClubDancerCounts = async () => {
    try {
      const counts = new Map();

      // Fetch all dancers
      const dancersResponse = await fetch(`https://${_host}:6003/api/${_company}/couples`);

      if (!dancersResponse.ok) {
        console.warn('Failed to fetch dancers for club statistics');
        return;
      }

      const dancers = await dancersResponse.json();

      // Count dancers per club
      dancers.forEach(dancer => {
        let clubId = null;

        // Try to match dancer with a club by clubId (support both field names)
        if (dancer.clubId || dancer.club_id) {
          clubId = dancer.clubId || dancer.club_id;
        } else if (dancer.clubName || dancer.club_name) {
          // Try to find club by name if no clubId
          const dancerClubName = dancer.clubName || dancer.club_name;
          const matchingClub = clubs.find(club => {
            const normalizedClub = normalizeClub(club);
            return normalizedClub.name && normalizedClub.name.toLowerCase() === dancerClubName.toLowerCase();
          });
          if (matchingClub) {
            clubId = matchingClub.id || matchingClub.club_id;
          }
        }

        if (clubId) {
          counts.set(clubId, (counts.get(clubId) || 0) + 1);
        }
      });

      setClubDancerCounts(counts);
      console.log(`Calculated dancer counts for ${counts.size} clubs`);
    } catch (error) {
      console.error('Error calculating club dancer counts:', error);
    }
  };

  // Fetch clubs from the API
  const fetchClubs = async () => {
    try {
      setLoading(true);

      let url;
      let data;

      // Try with competition filtering first
      try {
        url = selectedCompetition
          ? `https://${_host}:6003/api/${_company}/clubs?competitionId=${selectedCompetition.id}`
          : `https://${_host}:6003/api/${_company}/clubs`;
        const response = await fetch(url);

        if (response.ok) {
          data = await response.json();
          console.log('🔍 Clubs data received from API:', data);
          // Normalize clubs for consistent field access
          const normalizedClubs = data.map(club => {
            const normalized = normalizeClub(club);
            console.log('🔍 Club after normalization:', {
              original: club,
              normalized: normalized
            });
            return normalized;
          });
          setClubs(normalizedClubs);
          setLoading(false);
          return;
        }
      } catch (compFilterError) {
        console.log('Competition filtering not supported for clubs, trying fallback...');
      }

      // Fallback to all clubs (for servers without competition support)
      console.log('Fetching all clubs (competition filtering not available)');
      url = `https://${_host}:6003/api/${_company}/clubs`;
      const response = await fetch(url);

      if (!response.ok) {
        throw new Error('Failed to fetch clubs');
      }

      data = await response.json();

      // Filter clubs locally if server doesn't support it (support both field names)
      const filteredClubs = data.filter(club =>
        !selectedCompetition || club.competitionId === selectedCompetition.id || club.competition_id === selectedCompetition.id
      );

      // Normalize clubs for consistent field access
      const normalizedClubs = filteredClubs.map(club => normalizeClub(club));
      setClubs(normalizedClubs);
    } catch (err) {
      console.error('Error fetching clubs:', err);
      setError('Failed to load clubs. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Load clubs when component mounts or when competition changes
  useEffect(() => {
    fetchClubs();
  }, [selectedCompetition]);

  // Calculate dancer counts when clubs are loaded
  useEffect(() => {
    if (clubs.length > 0) {
      calculateClubDancerCounts();
    }
  }, [clubs]);

  // Force hide modal when in readOnly mode
  useEffect(() => {
    if (readOnly) {
      setShowModal(false);
      setEditingClub(null);
    }
  }, [readOnly]);

  // Handle delete club
  const handleDeleteClub = async (clubId, clubName) => {
    try {
      // Show confirmation dialog with club name
      const confirmDelete = window.confirm(
        `Are you sure you want to delete "${clubName}"? This action cannot be undone.`
      );

      if (!confirmDelete) {
        return;
      }

      const response = await fetch(`https://${_host}:6003/api/${_company}/clubs/${clubId}`, {
        method: 'DELETE'
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete club');
      }

      // Log club deletion
      if (currentUser) {
        auditLogger.log(
          currentUser.username || currentUser.id,
          'Delete Club',
          `Deleted club: ${clubName}`,
          clubId,
          'Club'
        );
      }

      setSuccess('Club deleted successfully');
      setTimeout(() => setSuccess(null), 3000);

      // Refresh clubs list
      fetchClubs();
    } catch (err) {
      console.error('Error deleting club:', err);
      setError(err.message);
      setTimeout(() => setError(null), 3000);
    }
  };

  // Handle Add New Club
  const handleAddClub = () => {
    setEditingClub(null);
    setShowModal(true);
  };

  // Handle modal close
  const handleModalClose = () => {
    setShowModal(false);
    setEditingClub(null);
  };

  // Handle print to PDF
  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;

    // Get the filtered clubs for the title
    const displayClubs = getFilteredClubs();
    const hasSearchQuery = searchQuery.trim() !== '';
    const totalDancers = displayClubs.reduce((sum, club) => sum + (clubDancerCounts.get(club.id) || 0), 0);

    // Create a new window for printing
    const printWindow = window.open('', '_blank');

    if (!printWindow) {
      alert('Please allow popups for this website to enable printing.');
      return;
    }

    // Prepare the HTML content for printing
    const printHTML = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Clubs Report</title>
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }

            body {
              font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
              font-size: 12px;
              color: #333;
              line-height: 1.4;
              padding: 20px;
            }

            .print-header {
              text-align: center;
              margin-bottom: 30px;
              border-bottom: 2px solid #3f51b5;
              padding-bottom: 20px;
            }

            .print-header h1 {
              color: #3f51b5;
              font-size: 24px;
              margin-bottom: 10px;
            }

            .print-header .subtitle {
              color: #666;
              font-size: 16px;
              margin-bottom: 15px;
            }

            .print-stats {
              display: flex;
              justify-content: center;
              gap: 30px;
              margin-bottom: 20px;
            }

            .print-stat {
              text-align: center;
              padding: 10px 20px;
              background: #f8f9fa;
              border-radius: 6px;
              border: 1px solid #e0e0e0;
            }

            .print-stat .number {
              display: block;
              font-size: 20px;
              font-weight: 700;
              color: #3f51b5;
              margin-bottom: 5px;
            }

            .print-stat .label {
              font-size: 11px;
              color: #666;
              font-weight: 500;
            }

            .search-info {
              text-align: center;
              margin-bottom: 20px;
              padding: 10px;
              background: #e8f5e8;
              border-radius: 6px;
              border: 1px solid #c8e6c9;
              color: #2e7d32;
              font-weight: 500;
            }

            table {
              width: 100%;
              border-collapse: collapse;
              margin-bottom: 20px;
              font-size: 11px;
            }

            th {
              background-color: #f8f9fa;
              color: #333;
              font-weight: 600;
              text-align: left;
              padding: 8px;
              border: 1px solid #ddd;
              font-size: 10px;
              text-transform: uppercase;
              letter-spacing: 0.5px;
            }

            td {
              padding: 6px 8px;
              border: 1px solid #ddd;
              vertical-align: middle;
            }

            tr:nth-child(even) {
              background-color: #f9f9f9;
            }

            tr:hover {
              background-color: #f5f5f5;
            }

            .print-footer {
              text-align: center;
              margin-top: 40px;
              padding-top: 20px;
              border-top: 1px solid #ddd;
              color: #666;
              font-size: 10px;
            }

            @media print {
              body { margin: 0.5in; }
              .print-header { page-break-after: avoid; }
              table { page-break-inside: avoid; }
              tr { page-break-inside: avoid; }
            }

            @page {
              margin: 0.5in;
              size: portrait;
            }
          </style>
        </head>
        <body>
          <div class="print-header">
            <h1>${hasSearchQuery ? 'Club Search Results' : 'Club/Studio Report'}</h1>
            <div class="subtitle">
              ${selectedCompetition ? `Competition: ${selectedCompetition.name}` : 'All Competitions'}
              ${hasSearchQuery ? `<br>Search: "${searchQuery}"` : ''}
            </div>
            <div class="print-stats">
              <div class="print-stat">
                <span class="number">${displayClubs.length}</span>
                <span class="label">${hasSearchQuery ? 'FOUND CLUBS' : 'TOTAL CLUBS'}</span>
              </div>
              <div class="print-stat">
                <span class="number">${totalDancers}</span>
                <span class="label">${hasSearchQuery ? 'TOTAL DANCERS (FOUND)' : 'TOTAL DANCERS'}</span>
              </div>
            </div>
            ${hasSearchQuery ? `<div class="search-info">Found ${displayClubs.length} club${displayClubs.length !== 1 ? 's' : ''} matching "${searchQuery}"</div>` : ''}
          </div>

          <table>
            <thead>
              <tr>
                <th>Club/Studio Name</th>
                <th>Contact Person</th>
                <th>Contact Number</th>
                <th>Province</th>
                <th>Dancer(s)/Couple(s)</th>
              </tr>
            </thead>
            <tbody>
              ${displayClubs
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(club => {
                  const normalizedClub = normalizeClub(club);
                  const dancerCount = clubDancerCounts.get(club.id) || 0;
                  return `
                    <tr>
                      <td><strong>${normalizedClub.name}</strong></td>
                      <td>${normalizedClub.contactPerson || '-'}</td>
                      <td>${normalizedClub.contactNumber || '-'}</td>
                      <td>${normalizedClub.province || '-'}</td>
                      <td style="text-align: center; font-weight: bold; color: ${dancerCount > 0 ? '#2e7d32' : '#999'};">${dancerCount}</td>
                    </tr>
                  `;
                }).join('')}
            </tbody>
          </table>

          <div class="print-footer">
            <p>Generated on ${new Date().toLocaleString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })}</p>
            <p>Flokraft Scrutiny System - Club/Studio Management Report</p>
          </div>
        </body>
      </html>
    `;

    // Write the HTML to the new window
    printWindow.document.write(printHTML);
    printWindow.document.close();

    // Wait for the content to load, then trigger print
    printWindow.onload = () => {
      setTimeout(() => {
        printWindow.print();
        // Close the window after printing (optional)
        printWindow.onafterprint = () => {
          printWindow.close();
        };
      }, 500);
    };
  };

  // Handle Edit Club
  const handleEditClub = (club) => {
    setEditingClub(club);
    setShowModal(true);
  };

  // Handle form submission (add/edit)
  const handleClubSubmitted = async (club) => {
    try {
      // Check if this is an update and the club name has changed
      if (editingClub && editingClub.name !== club.name) {
        // Update all dancers that have this club ID
        try {
          await updateDancersWithNewClubName(club.id, club.name, editingClub.name);
        } catch (dancerUpdateError) {
          console.warn('Failed to update dancers with new club name:', dancerUpdateError);
          // Continue with the club update even if dancer update fails
          // Don't throw the error, just log it
        }
      }

      // Log club creation or update
      if (currentUser && club) {
        if (editingClub) {
          // Update operation
          auditLogger.log(
            currentUser.username || currentUser.id,
            'Update Club',
            `Updated club: ${club.name}`,
            club.id,
            'Club'
          );
        } else {
          // Create operation
          auditLogger.log(
            currentUser.username || currentUser.id,
            'Create Club',
            `Created club: ${club.name}`,
            club.id,
            'Club'
          );
        }
      }

      // Refresh clubs list
      await fetchClubs();
      handleModalClose();
      setSuccess('Club saved successfully');
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      console.error('Error saving club:', err);
      setError(err.message || 'Failed to save club. Please try again.');
      setTimeout(() => setError(null), 3000);
    }
  };

  // Update all dancers with the new club name
  const updateDancersWithNewClubName = async (clubId, newClubName, oldClubName) => {
    try {
      // Fetch dancers filtered by club ID (server-side filtering now fixed)
      const response = await fetch(`https://${_host}:6003/api/${_company}/couples?clubId=${clubId}`);

      if (!response.ok) {
        throw new Error('Failed to fetch dancers for club update');
      }

      const dancers = await response.json();

      console.log(`Found ${dancers.length} dancers with clubId: ${clubId}`);

      if (dancers.length === 0) {
        console.log('No dancers found for this club');
        return;
      }

      console.log(`Updating ${dancers.length} dancers with new club name: ${oldClubName} -> ${newClubName}`);

      // Update each dancer with the new club name
      const updatePromises = dancers.map(async (dancer) => {
        const updateResponse = await fetch(`https://${_host}:6003/api/${_company}/couples/${dancer.id}`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            ...dancer,
            clubName: newClubName // Update the club name field
          })
        });

        if (!updateResponse.ok) {
          throw new Error(`Failed to update dancer: ${dancer.name}`);
        }

        return updateResponse.json();
      });

      await Promise.all(updatePromises);

      // Log the cascade update
      if (currentUser) {
        auditLogger.log(
          currentUser.username || currentUser.id,
          'Cascade Update Club Name',
          `Updated club name from "${oldClubName}" to "${newClubName}" for ${dancers.length} dancers`,
          clubId,
          'Club'
        );
      }

      console.log(`Successfully updated ${dancers.length} dancers with new club name`);

      // Trigger a refresh of couples in the CouplesList component if it exists
      // We'll use a custom event to communicate between components
      window.dispatchEvent(new CustomEvent('couplesDataChanged', {
        detail: { message: 'Dancers updated due to club name change' }
      }));

    } catch (err) {
      console.error('Error updating dancers with new club name:', err);
      throw new Error(`Failed to update dancers: ${err.message}`);
    }
  };

  return (
    <div className="clubs-list-container">
      <div className="clubs-list-header">
        <div className="header-left">
          <h2>{readOnly ? 'Registered Clubs' : 'Club/Studio Management'}</h2>
          <div className="search-container">
            <input
              type="text"
              placeholder="Search by name, contact person, number, or province..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
            {searchQuery && (
              <button
                className="clear-search-btn"
                onClick={() => setSearchQuery('')}
                title="Clear search"
              >
                ✕
              </button>
            )}
          </div>
        </div>
        <div className="header-buttons">
          {!readOnly && (
            <button className="add-club-btn" onClick={handleAddClub}>
              Add New Club
            </button>
          )}
          <button className="print-clubs-btn" onClick={handlePrint}>
            Print to PDF
          </button>
        </div>
      </div>

      {/* Display summary statistics - part of fixed header */}
      {!loading && (() => {
        const displayClubs = getFilteredClubs();
        const hasSearchQuery = searchQuery.trim() !== '';
        const totalDancers = displayClubs.reduce((sum, club) => sum + (clubDancerCounts.get(club.id) || 0), 0);

        if (displayClubs.length > 0) {
          return (
            <div className="clubs-summary">
              <div className="summary-stats">
                <div className="stat-item">
                  <span className="stat-number">{displayClubs.length}</span>
                  <span className="stat-label">{hasSearchQuery ? 'Found Clubs' : 'Total Clubs'}</span>
                </div>
                <div className="stat-item">
                  <span className="stat-number">{totalDancers}</span>
                  <span className="stat-label">{hasSearchQuery ? 'Total Dancers (Found)' : 'Total Dancers'}</span>
                </div>
              </div>
            </div>
          );
        }
        return null;
      })()}

      <div className="scrollable-content" ref={printRef}>
        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        {/* Club Modal - Only show when not in read-only mode */}
        {!readOnly && showModal && (
          <div className="club-modal-overlay" onClick={handleModalClose}>
            <div className="club-modal" onClick={(e) => e.stopPropagation()}>
              <div className="club-modal-header">
                <h3>{editingClub ? 'Edit Club/Studio' : 'Add New Club/Studio'}</h3>
                <button
                  className="modal-close-btn"
                  onClick={handleModalClose}
                >
                  ×
                </button>
              </div>
              <div className="club-modal-body">
                <ClubForm
                  existingClub={editingClub}
                  onClubSubmitted={handleClubSubmitted}
                  onCancel={handleModalClose}
                />
              </div>
            </div>
          </div>
        )}

        {loading ? (
          <div className="loading-message">Loading clubs...</div>
        ) : (
          (() => {
            const filteredClubs = getFilteredClubs();
            const hasSearchQuery = searchQuery.trim() !== '';

            if (filteredClubs.length === 0) {
              return (
                <div className="empty-message">
                  {hasSearchQuery
                    ? `No clubs found matching "${searchQuery}". Try a different search term.`
                    : readOnly
                      ? 'No clubs registered. Go to Administration to add clubs.'
                      : 'No clubs found. Add your first club to get started.'
                  }
                </div>
              );
            }

            return (
              <div>
                {hasSearchQuery && (
                  <div className="search-results-info">
                    Found {filteredClubs.length} club{filteredClubs.length !== 1 ? 's' : ''} matching "{searchQuery}"
                  </div>
                )}
                <div className="clubs-table-container">
                  <table className="clubs-table">
                    <thead>
                      <tr>
                        <th>Club/Studio Name</th>
                        <th>Contact Person</th>
                        <th>Contact Number</th>
                        <th>Province</th>
                        <th>Dancer(s)/Couple(s)</th>
                        {!readOnly && <th>Actions</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {filteredClubs
                        .sort((a, b) => {
                          // Sort by club name alphabetically
                          return a.name.localeCompare(b.name);
                        })
                        .map((club) => {
                          const normalizedClub = normalizeClub(club);
                          const dancerCount = clubDancerCounts.get(club.id) || 0;
                          return (
                            <tr key={club.id}>
                              <td className="club-name">
                                <span className="name-badge">
                                  {normalizedClub.name}
                                </span>
                              </td>
                              <td className="contact-person">
                                {normalizedClub.contactPerson || '-'}
                              </td>
                              <td className="contact-number">
                                <span className="phone-number">
                                  {normalizedClub.contactNumber || '-'}
                                </span>
                              </td>
                              <td className="province">
                                <span className="province-badge">
                                  {normalizedClub.province || '-'}
                                </span>
                              </td>
                              <td className="dancer-count">
                                <span className={`dancer-count-badge ${dancerCount > 0 ? 'has-dancers' : 'no-dancers'}`}>
                                  {dancerCount}
                                </span>
                              </td>
                              {!readOnly && (
                                <td className="club-actions">
                                  <button
                                    className="edit-club-btn"
                                    onClick={() => handleEditClub(club)}
                                    title={`Edit ${normalizedClub.name}`}
                                  >
                                    Edit
                                  </button>
                                  <button
                                    className="delete-club-btn"
                                    onClick={() => handleDeleteClub(club.id, normalizedClub.name)}
                                    title={`Delete ${normalizedClub.name}`}
                                  >
                                    Delete
                                  </button>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })()
        )}
      </div>
    </div>
  );
};

export default ClubsList;